import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.normalizeIndianMoney();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);

    if (newProductPrice) {
      const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
      const currentPrice = this.querySelector('[ref="priceContainer"]');
      if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);

      const currentNote = this.querySelector('.volume-pricing-note');
      const newNote = newProductPrice.querySelector('.volume-pricing-note');

      if (!newNote) {
        currentNote?.remove();
      } else if (!currentNote) {
        this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
      } else {
        currentNote.replaceWith(newNote);
      }

      this.normalizeIndianMoney();
      return;
    }

    const variant = event.detail.resource;
    if (variant?.price != null) {
      this.#updatePriceFromVariant(variant);
    }
  };

  /**
   * @param {Record<string, unknown>} variant
   */
  #updatePriceFromVariant(variant) {
    const container = this.querySelector('[ref="priceContainer"]');
    if (!container) return;

    const info = this.#calcPriceInfo(variant, container);
    const priceEl = container.querySelector('.price');
    let compareEl = container.querySelector('.compare-price');
    let badgeEl = container.querySelector('.discount-badge');
    const discount = Number(container.dataset.discount) || 0;

    if (priceEl) {
      priceEl.dataset.moneyCents = String(info.price);
      priceEl.textContent = this.#formatMoney(info.price);
    }

    if (info.hasSale) {
      if (!compareEl) {
        compareEl = this.#createPricePart(container, 'compare-price', priceEl);
      }

      compareEl.dataset.moneyCents = String(info.compare);
      compareEl.textContent = this.#formatMoney(info.compare);
      compareEl.style.display = '';
    } else if (compareEl) {
      compareEl.textContent = '';
      compareEl.style.display = 'none';
    }

    if (discount > 0) {
      if (!badgeEl) {
        badgeEl = this.#createPricePart(container, 'discount-badge', compareEl || priceEl);
      }

      badgeEl.textContent = `${discount}% OFF`;
      badgeEl.style.display = '';
    } else if (badgeEl) {
      badgeEl.style.display = 'none';
    }
  }

  /**
   * @param {Element} container
   * @param {string} className
   * @param {Element | null} afterElement
   */
  #createPricePart(container, className, afterElement) {
    const element = document.createElement('span');
    element.className = className;

    if (afterElement) {
      afterElement.insertAdjacentElement('afterend', element);
    } else {
      container.appendChild(element);
    }

    return element;
  }

  /**
   * @param {Record<string, unknown>} variant
   * @param {Element} container
   */
  #calcPriceInfo(variant, container) {
    const discount = Number(container.dataset.discount) || 0;
    const originalPrice = Number(variant.price);
    let finalPrice = originalPrice;
    let finalCompare = 0;
    let hasSale = false;

    if (discount > 0) {
      finalPrice = Math.round((originalPrice * (100 - discount) / 100) / 100) * 100;
      finalCompare = originalPrice;
      hasSale = true;
    } else if (variant.compare_at_price && Number(variant.compare_at_price) > originalPrice) {
      finalPrice = originalPrice;
      finalCompare = Number(variant.compare_at_price);
      hasSale = true;
    }

    return { price: finalPrice, compare: finalCompare, hasSale };
  }

  /**
   * @param {number} cents
   */
  #formatMoney(cents) {
    const formatter = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return `₹${formatter.format(cents / 100)}`;
  }

  normalizeIndianMoney() {
    const formatter = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    this.querySelectorAll('[data-money-cents]').forEach((price) => {
      const cents = Number(price.dataset.moneyCents);
      if (!price.dataset.moneyCents || !Number.isFinite(cents) || cents <= 0) return;

      const currentText = price.textContent || '';
      if (!/\d{1,3},\d{3}\.\d{2}/.test(currentText)) return;

      price.textContent = currentText.replace(
        /(\d[\d,]*)(\.\d{2})/,
        formatter.format(cents / 100)
      ).replace(/\bRs\.\s*/g, '₹');
    });
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
